/**
 * Leighton Parts collection behaviour: filtering, sorting, the mobile filter drawer and
 * progressive load-more.
 *
 * Everything here is an enhancement over markup that already works without it. The facet
 * form is a real GET form, the sort control is a real select inside it, and load-more is a
 * real link to the next page. If this file fails to load or throws, the collection still
 * filters, sorts and paginates — it just does full page loads.
 *
 * Filtering and sorting go through the Section Rendering API so the rail, the counts and
 * the grid update together from one response. Load-more fetches the next paginated page
 * and appends, because appending is the one case where re-rendering the section would
 * throw away the products already on screen.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class LpCollection extends HTMLElement {
  #sectionId = '';
  #abort = null;
  #previouslyFocused = null;

  connectedCallback() {
    this.#sectionId = this.dataset.sectionId || '';

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);

    // The no-JS submit button is inside <noscript>, so nothing to hide here; but a stray
    // Enter keypress in the price fields would do a native submit and full page load, so
    // submission is intercepted and routed through the same fetch path as everything else.
    this.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#closeDrawer({ restoreFocus: false });
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-lp-sort]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', /** @type {HTMLSelectElement} */ (target).value);
      url.searchParams.delete('page');
      this.#render(url);
      return;
    }

    const form = target.closest('[data-lp-facets-form]');
    if (form instanceof HTMLFormElement) this.#submitFacets(form);
  };

  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-lp-facets-form]')) return;
    event.preventDefault();
    this.#submitFacets(form);
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.closest('[data-lp-filter-trigger]')) {
      this.#openDrawer();
      return;
    }

    if (target.closest('[data-lp-filter-close]')) {
      this.#closeDrawer();
      return;
    }

    const more = target.closest('[data-lp-load-more]');
    if (more instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.#loadMore(more);
    }
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    const drawer = this.querySelector('[data-lp-filter-drawer]');
    if (drawer instanceof HTMLElement && !drawer.hidden) this.#closeDrawer();
  };

  /**
   * Turns the facet form into a URL and re-renders. Empty values are dropped so the query
   * string carries only active filters — otherwise every unchecked box would add a bare
   * `filter.v...=` pair and the URL would stop being shareable or canonical.
   * @param {HTMLFormElement} form
   */
  #submitFacets(form) {
    const url = new URL(window.location.href);
    const keptParams = new URLSearchParams();

    // A price input at its bound is not a filter — see the data-lp-bound note in
    // lp-facets.liquid. Collected by name so the FormData pass below can skip them.
    const atBound = new Set();
    for (const input of form.querySelectorAll('[data-lp-bound]')) {
      if (input instanceof HTMLInputElement && input.value === input.dataset.lpBound) {
        atBound.add(input.name);
      }
    }

    for (const [key, value] of new FormData(form).entries()) {
      if (typeof value !== 'string' || value.trim() === '') continue;
      if (atBound.has(key)) continue;
      keptParams.append(key, value);
    }

    url.search = keptParams.toString();
    url.searchParams.delete('page');
    this.#render(url);
  }

  /**
   * Fetches the section for `url`, swaps this element's contents, and pushes the URL so the
   * filtered view is shareable and the back button works.
   * @param {URL} url
   */
  async #render(url) {
    // A fast second change while the first is in flight would otherwise race: whichever
    // response landed last would win, which is not necessarily the latest request.
    this.#abort?.abort();
    this.#abort = new AbortController();

    this.dataset.loading = 'true';

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.#sectionId);

    try {
      const response = await fetch(fetchUrl, { signal: this.#abort.signal });
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('lp-collection');
      if (!next) throw new Error('Section response contained no lp-collection element');

      this.innerHTML = next.innerHTML;
      window.history.replaceState({}, '', url);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // A failed enhancement must not strand the shopper on a stale grid, so fall back to
      // the navigation the form would have done natively.
      window.location.assign(url);
    } finally {
      delete this.dataset.loading;
    }
  }

  /**
   * Appends the next page's cards rather than replacing the grid, then swaps the button for
   * the next page's own (or removes it on the last page).
   * @param {HTMLAnchorElement} trigger
   */
  async #loadMore(trigger) {
    const grid = this.querySelector('[data-lp-grid]');
    if (!grid) return;

    trigger.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(trigger.href);
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = html.querySelectorAll('[data-lp-grid] > li');
      const firstNew = items[0];

      grid.append(...items);

      const nextTrigger = html.querySelector('[data-lp-load-more]');
      const container = trigger.parentElement;
      if (nextTrigger && container) {
        container.replaceChildren(nextTrigger);
      } else {
        container?.remove();
      }

      // Focus the first appended card so a keyboard user continues from the new content
      // instead of being dropped at the top of the document when the button disappears.
      const link = firstNew?.querySelector('a[href]');
      if (link instanceof HTMLElement) {
        link.setAttribute('tabindex', '-1');
        link.focus({ preventScroll: true });
      }
    } catch {
      window.location.assign(trigger.href);
    } finally {
      trigger.removeAttribute('aria-busy');
    }
  }

  #openDrawer() {
    const drawer = this.querySelector('[data-lp-filter-drawer]');
    const trigger = this.querySelector('[data-lp-filter-trigger]');
    if (!(drawer instanceof HTMLElement)) return;

    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    drawer.hidden = false;
    // Forced reflow before adding the open class so the panel transitions in from its
    // closed transform rather than appearing already-open on first paint.
    if (!REDUCED_MOTION.matches) void drawer.offsetWidth;
    drawer.dataset.open = 'true';

    trigger?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    const focusable = drawer.querySelector('button, input, select, a[href], summary');
    if (focusable instanceof HTMLElement) focusable.focus();

    document.addEventListener('focusin', this.#enforceFocus, true);
  }

  #closeDrawer({ restoreFocus = true } = {}) {
    const drawer = this.querySelector('[data-lp-filter-drawer]');
    const trigger = this.querySelector('[data-lp-filter-trigger]');

    document.removeEventListener('focusin', this.#enforceFocus, true);
    document.body.style.overflow = '';

    if (drawer instanceof HTMLElement) {
      delete drawer.dataset.open;
      drawer.hidden = true;
    }
    trigger?.setAttribute('aria-expanded', 'false');

    if (restoreFocus && this.#previouslyFocused && document.contains(this.#previouslyFocused)) {
      this.#previouslyFocused.focus();
    }
    this.#previouslyFocused = null;
  }

  /**
   * Keeps focus inside the open drawer. Scoped to this instance rather than a shared
   * module-level handler: Horizon's own trap keeps its handlers in one module-level object
   * and tears them down from any drawer's disconnect, which is exactly why its cart drawer
   * loses its trap. This one cannot be disarmed by another component.
   */
  #enforceFocus = (event) => {
    const drawer = this.querySelector('[data-lp-filter-drawer]');
    if (!(drawer instanceof HTMLElement) || drawer.hidden) return;
    if (event.target instanceof Node && drawer.contains(event.target)) return;

    const focusable = drawer.querySelector('button, input, select, a[href], summary');
    if (focusable instanceof HTMLElement) focusable.focus();
  };
}

if (!customElements.get('lp-collection')) {
  customElements.define('lp-collection', LpCollection);
}
